import el from "@cypherpotato/el";
import page from "page";
import { authenticateWithLoginKey, logoutCurrentAccountAndLoginNext } from "../account-switching";
import { api } from "../api";
import { Cached, ClearCache } from "../cache";
import { currentPath } from "../global-state";
import { DisplayCreditAdditionModal } from "../helpers/invoice-helpers";
import { getCurrentLoginKey, getSavedAccounts, getSession, isAuthed } from "../session";
import { displayAlert, displayFinder, displayFormControl } from "../ui/alert";
import { showFlyout } from "../ui/flyout";
import { formatCurrency } from "../ui/format";
import { runOperation } from "../ui/operation";
import { Button, ButtonRow } from "./button";
import { Icon } from "./icon";
import { MiniApp } from "./mini-app";
import { ModelPickerControl } from "./model-picker";
import { MutedText, TextHeading } from "./text";
import { IconButton, IconButtonGrid } from "./icon-button";
import { showDialog } from "@codemirror/view";
import { Dropdown, DropdownDivider, DropdownItem } from "./dropdown";
import { SpecializedControl } from "./specializedObject";
import { TextInput } from "./form";
import { aviAssistantVisible, initializeAviAssistant, toggleAviAssistant } from "./avi-assistant";
import { setManagedInterval } from "../interval-manager";
import { cond } from "../util";
import { Badge } from "./badge";
import { NotificationItem, NotificationsWrapper } from "./notification";

function UpdatingUsage() {

    const usage = el.state.create(null);

    async function getCachedValue() {
        return await Cached('account_balance', async () => {
            if (!isAuthed()) {
                return 0;
            }

            const usageResp = await api.getJson("/api/v1/information/balance");
            return usageResp.data;
        }, 10_000)
    }

    async function fetchUsage() {
        usage.value = await getCachedValue();
    }

    setInterval(fetchUsage, 10_000);
    fetchUsage();

    return el.state.listening([usage], () =>
        el("span",
            usage.value && formatCurrency(usage.value.balance)
        )
    );
}

function UpdatingNotificationCount() {

    const notifyCount = el.state.create(null);

    async function getCachedValue() {
        return await Cached('account_notification_count', async () => {
            if (!isAuthed()) {
                return 0;
            }

            const response = await api.getJson("/api/v1/notifications/unread/count");
            return response.data.count;
        }, 10_000)
    }

    async function fetchNotificationCount() {
        notifyCount.value = await getCachedValue();
    }

    setInterval(fetchNotificationCount, 10_000);
    fetchNotificationCount();

    return el.state.listening([notifyCount], () =>
        notifyCount.value && Badge(
            cond(
                [notifyCount.value > 99, "+99"],
                [true, notifyCount.value]
            ),
            "var(--warn)"
        ) || el("style")
    );
}

export function DashboardNavGroup(label) {
    return el("div.dashboard-nav-group", label);
}

export function DashboardNavItem(icon, label, href, extraText) {
    return el.state.listening([currentPath], () =>
        el("div.dashboard-nav-item",
            Icon(icon),
            el("span", label),
            {
                onMouseUp(event) {
                    if (event.button === 0) {
                        page(href);
                    } else if (event.button === 1) {
                        event.preventDefault();
                        window.open(href, "_blank");
                    }
                },
                init() {
                    if (currentPath.value.replace(/\/$/, "") === href) {
                        this.classList.add("active");
                    }
                }
            },
            extraText && el(".extra-texts", extraText)
        )
    );
}

function DashboardSearchButton() {
    return el("div.dashboard-nav-item.float-button.platform-search-button",
        Icon("search-line"),
        {
            ariaLabel: "Search platform",
            onClick() {
                runOperation("Loading search options...", async () => {
                    const response = await api.getJson("/api/v1/platform/search");
                    displayFinder({
                        placeholder: "Search collections, gateways, chat clients, batch, and models...",
                        options: (response?.data ?? []).map(result => ({
                            id: `${result.type}:${result.id}`,
                            title: result.title,
                            description: result.description,
                            group: cond(
                                [result.type === "rag-collection", "RAG Collections"],
                                [result.type === "ai-gateway", "AI Gateways"],
                                [result.type === "chat-client", "Chat Clients"],
                                [result.type === "batch-workflow", "Batch Workflows"],
                                [result.type === "batch-job", "Batch Jobs"],
                                [result.type === "model", "Model"],
                                [true, "search-line"]
                            ),
                            icon: Icon(cond(
                                [result.type === "rag-collection", "book-shelf-line"],
                                [result.type === "ai-gateway", "route-line"],
                                [result.type === "chat-client", "chat-1-line"],
                                [result.type === "batch-workflow", "list-check-3"],
                                [result.type === "batch-job", "play-list-add-line"],
                                [result.type === "model", "instance-line"],
                                [true, "search-line"]
                            )),
                            url: result.url
                        })),
                        onSelect(_id, result) {
                            page(result.url);
                        }
                    });
                });
            }
        }
    )
}

function DashboardNotificationsButton() {
    return el("div.dashboard-nav-item.float-button.notification-button",
        Icon("notification-4-line"),
        UpdatingNotificationCount(),
        {
            onClick() {
                runOperation("Listing notifications...", async () => {
                    const notifications = await api.getJson("/api/v1/notifications");
                    ClearCache("account_notification_count");

                    showFlyout(
                        NotificationsWrapper(
                            ...notifications.data.map(NotificationItem)
                        )
                    )
                });
            }
        }
    )
}

export function DashboardExternalNavItem(icon, label, href) {
    return el.state.listening([currentPath], () =>
        el("div.dashboard-nav-item",
            Icon(icon),
            el("span", label, Icon("external-link-line")),
            {
                onClick() {
                    window.open(href, "_blank");
                }
            }
        )
    );
}

export function DashboardActionNavItem(icon, label, action) {
    return el.state.listening([currentPath], () =>
        el("div.dashboard-nav-item",
            Icon(icon),
            el("span", label),
            {
                onClick: action
            }
        )
    );
}

export function Dashboard(...children) {

    const session = getSession();
    var currentPlan = window.authenticationDetails?.plan || session.data.plan;
    const aviAssistantAvailable = currentPlan === "Pro" || currentPlan === "Max";
    const currentLoginKey = getCurrentLoginKey();
    const savedAccounts = getSavedAccounts()
        .filter(account => account.loginKey !== currentLoginKey);

    function AccountAvatar(account, size = 32) {
        return el(".account-avatar-icon.small",
            el("img", { src: `https://0.gravatar.com/avatar/${account.data?.emailSha256 || ""}?size=${size}` })
        );
    }

    function SavedAccountItem(account, index) {
        return DropdownItem(
            account.data?.name || `Account ${index + 1}`,
            AccountAvatar(account),
            {
                onClick() {
                    runOperation("Switching account...", async () => {
                        await authenticateWithLoginKey(account.loginKey);
                        window.location.assign("/dashboard");
                    }).catch(() => { });
                }
            }
        );
    }

    return el("div.page-dashboard",
        {
            mount() {
                if (aviAssistantAvailable) {
                    initializeAviAssistant();
                }
            }
        },
        el("aside",
            el("menu.logo-wrapper",
                el("img", { src: "/assets/aivax-alt.png" }),
                {
                    onClick() {
                        page("/dashboard");
                    }
                }
            ),
            el("menu.links-container",

                el(".link-group",
                    DashboardSearchButton(),
                    DashboardNotificationsButton(),
                    DashboardNavItem("home-2-line", "Home", "/dashboard"),
                ),

                DashboardNavGroup("Cloud AI"),
                el(".link-group",
                    DashboardNavItem("instance-line", "Models", "/dashboard/models"),
                    DashboardNavItem("route-line", "AI Gateways", "/dashboard/ai-gateways"),
                    DashboardNavItem("book-shelf-line", "RAG Collections", "/dashboard/collections"),
                    DashboardNavItem("chat-1-line", "Chat clients", "/dashboard/chat-clients"),
                    DashboardNavItem("list-check-3", "Batch", "/dashboard/batch"),
                    DashboardNavItem("ai-generate-2-line", "Generations", "/dashboard/generations"),
                    DashboardNavItem("flask-line", "Tests", "/dashboard/tests"),
                    DashboardNavItem("voiceprint-line", "Voice", "/dashboard/voice"),
                ),
                // DashboardActionNavItem("pencil-ruler-2-line", "Tools", () => {
                //     showFlyout(
                //         IconButtonGrid(
                //             IconButton({
                //                 icon: Icon("ai-generate-2-line"),
                //                 label: "Prompt builder",
                //                 color: "#177e53",
                //                 onClick() {
                //                     page("/dashboard/tools/sysin-builder");
                //                 }
                //             })
                //         )
                //     )
                // }),

                DashboardNavGroup("Management"),
                el(".link-group",
                    session.data.isAdmin && DashboardNavItem("shield-user-line", "Admin", "/dashboard/admin/accounts"),
                    DashboardNavItem("bar-chart-2-line", "Analytics", "/dashboard/analytics"),
                    DashboardNavItem("wallet-2-line", "Usage and limits", "/dashboard/usage"),
                    DashboardNavItem("dropdown-list", "Conversations", "/dashboard/conversations"),
                    DashboardNavItem("lightbulb-ai-line", "Skills", "/dashboard/skills"),
                    // DashboardNavItem("book-shelf-line", "Instruction Store", "/dashboard/instruction-store"),
                    DashboardNavItem("file-list-3-line", "Logs", "/dashboard/logs"),
                )
            ),
            el("menu.bottom.links-container",
                el(".link-group",
                    aviAssistantAvailable && el.state.listening([aviAssistantVisible], () =>
                        el("div.dashboard-nav-item",
                            {
                                classList: [aviAssistantVisible.value && "avi-assistant-active"],
                                onClick() {
                                    toggleAviAssistant();
                                }
                            },
                            Icon("robot-2-line"),
                            el("span", "Avi Assistant")
                        )
                    ),
                    DashboardExternalNavItem("git-repository-line", "Documentation", "https://docs.aivax.net/docs/overview"),
                    DashboardExternalNavItem("pulse-line", "Status", "https://status.aivax.net/"),
                )
            ),
            el("menu.bottom.account-container",
                Dropdown(
                    el(".account-container",
                        el(".account-info",
                            el("strong", session.data.name),
                            el("span.plan-badge", currentPlan, { dataPlanName: currentPlan })
                        ),
                        el(".account-avatar-icon",
                            el("img", { src: `https://0.gravatar.com/avatar/${session.data.emailSha256}?size=128` })
                        )
                    ),

                    ...savedAccounts.map(SavedAccountItem),
                    DropdownItem("Add account", Icon("login-circle-line"), {
                        onClick() {
                            displayFormControl({
                                title: "Log in to another account",
                                message: "Enter the login key for the account you want to add.",
                                formControlItem: SpecializedControl({
                                    fields: [
                                        {
                                            label: "Login key",
                                            name: "loginKey",
                                            control: TextInput(null, null, {
                                                placeholder: "14-digits key",
                                                // type: 'password'
                                            })
                                        }
                                    ]
                                }),
                                onConfirm(data) {
                                    const loginKey = String(data.loginKey || "").trim();
                                    runOperation("Adding account...", async () => {
                                        await authenticateWithLoginKey(loginKey);
                                        window.location.assign("/dashboard");
                                    }).catch(() => { });
                                },
                                validate: [
                                    [x => String(x.loginKey || "").trim().length === 14, "Login key must be 14 characters long."]
                                ]
                            })
                        }
                    }),
                    DropdownDivider(),
                    DropdownItem("Manage account", Icon("settings-4-line"), {
                        onClick() {
                            page("/dashboard/account")
                        }
                    }),
                    DropdownItem("Logout", Icon("logout-circle-line"), {
                        onClick() {
                            runOperation("Logging out...", async () => {
                                try {
                                    const loggedNextAccount = await logoutCurrentAccountAndLoginNext();
                                    window.location.assign(loggedNextAccount ? "/dashboard" : "/");
                                } catch {
                                    window.location.assign("/");
                                }
                            });
                        }
                    }),
                )
            )
        ),
        el('div.page-contents',
            el("main",
                el(".main-contents",
                    ...children
                ),
                el("footer",
                    el(".footer-description",
                        el(".footer-item",
                            el("img[src=/assets/aivax-alt.png]",),
                            el("p",
                                "AIVAX is an inference and agents provider located in Brazil."
                            )
                        )
                    ),
                    el(".footer-row",
                        el("a[href='https://aivax.net/'][target='_blank']",
                            "Landing Page"
                        ),
                        el("a[href='https://docs.aivax.net/docs/overview'][target='_blank']",
                            "Documentation"
                        ),
                        el("a[href='https://docs.aivax.net/docs/legal/privacy-policy'][target='_blank']",
                            "Privacy policy"
                        ),
                        el("a[href='https://docs.aivax.net/docs/legal/terms-of-service'][target='_blank']",
                            "Terms of use"
                        ),
                        el("a[href='https://status.aivax.net/'][target='_blank']",
                            "Status"
                        )
                    )
                )
            )
        ),
    );
}

export function DashboardTitle(title, subtitle) {
    return el("div.dashboard-title",
        el("h1", title),
        el("p", subtitle),
        el("div.dashboard-title-separator")
    );
}
